import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { useState } from 'react';
import { Badge, Button, Checkbox, ErrorMessage, Spinner, unitMoney } from '../ui';

interface Option {
  key: string;
  label: string;
  price: string;
  profitEach: string | null;
  marginPct: number | null;
  vsSoldPct: number | null;
  basis: string;
  warnings: string[];
  recommended: boolean;
}
interface Suggestions {
  currency: string;
  priceLocked: boolean;
  priceRule: string | null;
  options: Option[];
  notes: string[];
  facts: {
    avgCost: string | null;
    soldAvg: string | null;
    soldPieces: number | null;
    forSaleAvg: string | null;
    forSalePieces: number | null;
    sellThroughPct: number | null;
    soldPerMonth: number | null;
    yourMonthsOfStock: number | null;
    marketAge: string | null;
    current: string | null;
  };
}

const sign = (n: number) => `${n > 0 ? '+' : ''}${n}%`;

/** Price options for a lot, worked out from its cost and BrickLink's figures. Choosing one saves it as the lot's price. */
export function PriceSuggestionsModal({ lotId, writable, onClose }: { lotId: number; writable: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['price-suggestions', lotId], queryFn: () => api<Suggestions>(`/api/v1/lots/${lotId}/price-suggestions`) });
  const done = () => {
    void qc.invalidateQueries({ queryKey: ['lot', String(lotId)] });
    void qc.invalidateQueries({ queryKey: ['lots'] });
    void qc.invalidateQueries({ queryKey: ['dashboard'] });
    void qc.invalidateQueries({ queryKey: ['price-suggestions', lotId] });
  };
  // Start with tracking on for every suggestion; the user can explicitly opt out for this one choice.
  const [keepChoice, setKeepChoice] = useState<boolean | null>(null);
  // New inventory should follow the selected price plan unless the user explicitly opts out.
  const keep = keepChoice ?? true;
  const use = useMutation({
    mutationFn: ({ price, rule }: { price: string; rule: string }) => api(`/api/v1/lots/${lotId}`, { method: 'PATCH', body: { price, priceRule: keep ? rule : null } }),
    onSuccess: () => {
      done();
      onClose();
    },
  });
  const fetchMarket = useMutation({
    mutationFn: () => api(`/api/v1/lots/${lotId}/market/refresh`, { method: 'POST' }),
    onSuccess: done,
  });
  const s = q.data;
  const f = s?.facts;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Suggested prices"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
      onKeyDown={(e) => e.key === 'Escape' && onClose()}
    >
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white p-5 shadow-xl">
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-lg font-semibold">Suggested prices</h2>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
        {q.isPending && <Spinner />}
        {q.error && <ErrorMessage error={q.error} />}
        {s && f && (
          <>
            <p className="mt-2 text-sm text-slate-600">
              {f.avgCost ? <>Cost {unitMoney(f.avgCost)} each. </> : <>No cost known. </>}
              {f.soldAvg && (
                <>
                  Selling for {unitMoney(f.soldAvg)} on BrickLink
                  {f.sellThroughPct !== null && <>, {f.sellThroughPct}% sell-through</>}
                  {f.soldPerMonth ? <>, about {f.soldPerMonth.toLocaleString('en-GB')} a month</> : null}
                  {f.marketAge && <> ({f.marketAge})</>}.{' '}
                </>
              )}
              {f.yourMonthsOfStock !== null && (
                <>Your stock would last {f.yourMonthsOfStock < 1 ? 'under a month' : `about ${f.yourMonthsOfStock} ${f.yourMonthsOfStock === 1 ? 'month' : 'months'}`} at that pace. </>
              )}
              {f.current && <>Priced now at {unitMoney(f.current)}.</>}
            </p>
            <p className="text-xs text-slate-500">Before BrickLink fees and postage.</p>

            {s.notes.map((n) => (
              <p key={n} className="mt-2 rounded bg-amber-50 p-2 text-sm text-amber-900">
                {n}
              </p>
            ))}
            {!f.soldAvg && writable && (
              <div className="mt-2">
                <Button variant="secondary" disabled={fetchMarket.isPending} onClick={() => fetchMarket.mutate()}>
                  {fetchMarket.isPending ? 'Asking BrickLink…' : 'Get BrickLink figures now'}
                </Button>
                {fetchMarket.error && <ErrorMessage error={fetchMarket.error} />}
              </div>
            )}

            {writable && !s.priceLocked && (
              <div className="mt-3 rounded bg-slate-50 p-2 text-sm">
                <Checkbox label="Keep the price I choose up to date" checked={keep} onChange={setKeepChoice} />
                <p className="mt-1 text-xs text-slate-600">
                  {s.priceRule ? `This lot follows the "${s.options.find((o) => o.key === s.priceRule)?.label ?? s.priceRule}" rule now. ` : ''}
                  When BrickLink's figures are refreshed overnight, the price follows the same option: it never goes below the floor, ignores small moves, waits if the figures are old or thin, and every change is listed on the Pricing page. Setting the price by hand switches it off.
                </p>
              </div>
            )}
            <ul className="mt-3 space-y-2">
              {s.options.map((o) => (
                <li key={o.key} className={`rounded border p-3 ${o.recommended ? 'border-blue-500 bg-blue-50' : 'border-slate-200'}`}>
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <span className="font-medium">{o.label}</span> {o.recommended && <Badge tone="blue">Suggested</Badge>}
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-lg font-semibold">{unitMoney(o.price)}</span>
                      {writable && (
                        <Button
                          disabled={use.isPending || s.priceLocked}
                          title={s.priceLocked ? 'The price is locked. Unlock it on the lot first.' : undefined}
                          onClick={() => use.mutate({ price: o.price, rule: o.key })}
                        >
                          Use this
                        </Button>
                      )}
                    </div>
                  </div>
                  <p className="mt-1 text-sm text-slate-600">{o.basis}</p>
                  <p className="mt-1 text-xs text-slate-500">
                    {o.profitEach !== null && <>Profit {unitMoney(o.profitEach)} each ({o.marginPct}% margin). </>}
                    {o.vsSoldPct !== null && <>{sign(o.vsSoldPct)} against recent sales.</>}
                  </p>
                  {o.warnings.map((w) => (
                    <p key={w} className="mt-1 text-xs font-medium text-red-700">
                      {w}
                    </p>
                  ))}
                </li>
              ))}
              {s.options.length === 0 && <li className="text-sm text-slate-600">There is nothing to base a price on yet.</li>}
            </ul>
            {s.priceLocked && <p className="mt-2 text-sm text-slate-600">The price on this lot is locked, so nothing here can change it.</p>}
            {use.error && <ErrorMessage error={use.error} />}
          </>
        )}
      </div>
    </div>
  );
}
